import React from 'react'
import ScrollableAnchor from 'react-scrollable-anchor'
import RichtextParser from '../../utils/richtext-parser'
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed/'

import Intro7 from '../Intro7'
import LazyImage from '../../components/lazy-image'
import { Box } from '../UIKit/system'
import LeftImageRightContent from '../../components/left-image-right-content'
import SectionTimeline from '../../components/section-timeline'
import SectionQuote from '../../components/section-quote'
import CourseSignup from '../course-signup'
import QuoteCarousel from '../quotes-carousel/index'
import { filterByProp } from '../../utils/array'
import parseStoryblokLink from '../../utils/parse-storyblok-link'

type AttendanceOption = 'face_to_face' | 'virtual' | 'on_demand'

interface StoryblokCoursePageBody {
  courseTitle: string;
  introFullWidthImage: Record<any, any>;
  aboutTitle: string;
  aboutImage: Record<any, any>;
  aboutText: string;
  accreditations?: string;
  attendanceOptions?: AttendanceOption[];
  courseIntroSubtitle: string;
  ctaBlockTitle?: string;
  ctaBlockText?: string;
  courseLength: string;
  courseLengthSubtitle: string;
  courseProgramme: unknown[];
  introCtaText: string;
  signupFormId: string;
  signupFormPortalId: string;
  signupFormSubtext: unknown;
  signupFormTitle: string;
  quotes: {
    quote: string;
    quotee: string;
    quoteeSubtitle: string;
    largeQuoteMode: boolean;
  }[];
  watchLink?: string;
}

function StoryblokCoursePage(body: StoryblokCoursePageBody) {
  let fluidFullWidthImg: ReturnType<typeof getFluidGatsbyImage> | undefined
  let aboutImage: ReturnType<typeof getFluidGatsbyImage> | undefined

  const showSignUp =
    body.signupFormTitle && body.signupFormPortalId && body.signupFormId
  const watchLink = parseStoryblokLink(body.watchLink)

  if (body.introFullWidthImage !== undefined) {
    fluidFullWidthImg = getFluidGatsbyImage(body.introFullWidthImage, {
      maxWidth: 1920
    })
  }

  if (body.aboutImage !== undefined) {
    aboutImage = getFluidGatsbyImage(body.aboutImage, {
      maxWidth: 800
    })
  }

  const attendanceOptionsText = () => {
    const mapAttendanceOptions: Record<AttendanceOption, string> = {
      face_to_face: 'Face to Face',
      virtual: 'Virtual',
      on_demand: 'On Demand'
    }

    return body.attendanceOptions
      ?.map((v) => mapAttendanceOptions[v])
      .join(' | ')
  }

  return (
    <>
      <Intro7
        pagination={[
          { link: '/courses/', name: 'Sertus Training' },
          { name: 'Course details' }
        ]}
        title={body.courseTitle}
        body={body.courseIntroSubtitle}
        cta={{
          title: body.ctaBlockTitle || '',
          body: body.ctaBlockText || '',
          links: [
            {
              to: '#form',
              title: body.introCtaText,
              active: showSignUp
            },
            {
              to: watchLink,
              title: 'Watch Now',
              active: watchLink && watchLink !== '#',
              target: '_blank',
              iconLeft: 'playCircle'
            }
          ].filter((v) => filterByProp(v, 'active'))
        }}
        infoContent={[
          {
            title: 'Attendance options',
            text: attendanceOptionsText()
          },
          {
            title: 'Estimated time',
            text: body.courseLength
          },
          {
            title: 'Accreditations',
            text: body.accreditations
          }
        ].filter((info) => filterByProp(info, 'text'))}
      />

      {body.introFullWidthImage && <LazyImage fluid={fluidFullWidthImg} />}
      <Box>
        <LeftImageRightContent
          title={body.aboutTitle}
          paragraph={RichtextParser(body.aboutText)}
          image={aboutImage}
        />
        <SectionTimeline
          title={'Course programme.'}
          contentTitle={'Course length:'}
          durationText={body.courseLength}
          contentText={body.courseLengthSubtitle}
          items={body.courseProgramme}
        />
        {body.quotes.length !== 0 ? (
          body.quotes.length === 1 ? (
            <SectionQuote
              text={body.quotes[0].quote}
              author={body.quotes[0].quotee}
              company={body.quotes[0].quoteeSubtitle}
              largeQuoteMode={body.quotes[0].largeQuoteMode}
            />
          ) : (
            <QuoteCarousel quotes={body.quotes} />
          )
        ) : null}

        {showSignUp && (
          <ScrollableAnchor id="form">
            <div>
              <CourseSignup
                title={body.signupFormTitle}
                subtitle={RichtextParser(body.signupFormSubtext)}
                portalId={body.signupFormPortalId}
                formId={body.signupFormId}
              />
            </div>
          </ScrollableAnchor>
        )}
      </Box>
    </>
  )
}

export default StoryblokCoursePage
